import type { JSX } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { VerificationStatusBadge } from '@/features/verifications/components/VerificationStatusBadge';
import { InspectionClaim } from './InspectionClaim';
import { InspectionAnswers } from './InspectionAnswers';
import { InspectionActions } from './InspectionActions';
import { InspectionOutcome } from './InspectionOutcome';
import type { InspectionVerification } from '../types/inspection.types';

interface InspectionCardProps {
  verification: InspectionVerification;
  isProcessing: boolean;
  onApprove: (verificationId: string) => void;
  onReturn: (verification: InspectionVerification) => void;
}

export function InspectionCard({
  verification,
  isProcessing,
  onApprove,
  onReturn,
}: InspectionCardProps): JSX.Element {
  const isAwaitingInspection = verification.status === 'ACCEPTED';

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <InspectionClaim verification={verification} />
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{verification.refereeName}</span>
              <VerificationStatusBadge status={verification.status} />
            </div>
            <p className="text-sm text-muted-foreground">{verification.refereeEmail}</p>
            {verification.refereeJobTitle && (
              <p className="text-sm text-muted-foreground">{verification.refereeJobTitle}</p>
            )}
            {verification.refereeComment && (
              <p className="text-sm italic">{verification.refereeComment}</p>
            )}
          </div>
          <InspectionAnswers answers={verification.refereeAnswers} />
          <InspectionOutcome verification={verification} />
        </div>
        {isAwaitingInspection && (
          <InspectionActions
            verification={verification}
            isProcessing={isProcessing}
            onApprove={onApprove}
            onReturn={onReturn}
          />
        )}
      </CardContent>
    </Card>
  );
}
